import React, { useMemo } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Typography,
  Autocomplete,
  Grid,
} from "@mui/material";

interface BeforeAfterBasicInfoFormProps {
  formData: {
    projectName: string;
    style: string | null;
    projectCategory: string | null;
    budgetRange: string | null;
    squareFootage: string | null;
    projectDuration: string | null;
    subtitle: string;
    specialRequirements: string[];
  };
  onFormDataChange: (field: string, value: any) => void;
}

const PROJECT_STYLES = [
  { value: "Modern", label: "Modern" },
  { value: "Contemporary", label: "Contemporary" },
  { value: "Traditional", label: "Traditional" },
  { value: "Transitional", label: "Transitional" },
  { value: "MidCentury", label: "Mid-Century Modern" },
  { value: "Industrial", label: "Industrial" },
  { value: "Scandinavian", label: "Scandinavian" },
  { value: "Bohemian", label: "Bohemian" },
  { value: "Farmhouse", label: "Farmhouse" },
  { value: "Rustic", label: "Rustic" },
  { value: "Mediterranean", label: "Mediterranean" },
  { value: "ArtDeco", label: "Art Deco" },
  { value: "Victorian", label: "Victorian" },
  { value: "Colonial", label: "Colonial" },
  { value: "Craftsman", label: "Craftsman" },
];

const PROJECT_CATEGORIES = [
  { value: "FullHomeDesign", label: "Full Home Design" },
  { value: "FamilyRoom", label: "Living Room" },
  { value: "LivingRoomDesign", label: "Living Room Design" },
  { value: "KitchenDesign", label: "Kitchen Design" },
  { value: "DiningRoomDesign", label: "Dining Room Design" },
  { value: "EntryDesign", label: "Entry Design" },
  { value: "MudroomDesign", label: "Mudroom Design" },
  { value: "HomeOffice", label: "Home Office" },
  { value: "KidsBedroom", label: "Kids Bedroom" },
  { value: "BasementDesign", label: "Basement Design" },
  { value: "SpacePlanning", label: "Space Planning" },
  { value: "ColorConsulting", label: "Color Consulting" },
  { value: "LightingDesign", label: "Lighting Design" },
  { value: "FurnitureSelection", label: "Furniture Selection" },
  { value: "ArtSelection", label: "Art Selection" },
  { value: "AntiqueSelection", label: "Antique Selection" },
  { value: "HomeStaging", label: "Home Staging" },
  { value: "ConstructionRemodel", label: "Construction/Remodel" },
  { value: "EventAndHolidayDecor", label: "Event/Holiday Decor" },
  { value: "PrimaryBedroomDesign", label: "Primary Bedroom Design" },
  { value: "GuestBedroomDesign", label: "Guest Bedroom Design" },
  { value: "PrimaryBathroomDesign", label: "Primary Bathroom Design" },
  { value: "OtherBathroomDesign", label: "Other Bathroom Design" },
  { value: "OutdoorSpaceDesign", label: "Outdoor Space Design" },
  { value: "ProjectManagementDesign", label: "Project Management + Design" },
];

const BUDGET_RANGES = [
  { value: "upToThreeK", label: "Up to $3,000" },
  { value: "ThreeKToFiveK", label: "$3,000 - $5,000" },
  { value: "FiveKToTenK", label: "$5,000 - $10,000" },
  { value: "TenKToFifteenK", label: "$10,000 - $15,000" },
  { value: "FifteenKToTwentyK", label: "$15,000 - $20,000" },
  { value: "TwentyKToThirtyK", label: "$20,000 - $30,000" },
  { value: "ThirtyKToFortyK", label: "$30,000 - $40,000" },
  { value: "FortyKToFiftyK", label: "$40,000 - $50,000" },
  { value: "FiftyKToSeventyFiveK", label: "$50,000 - $75,000" },
  { value: "SeventyFiveKToOneHundredK", label: "$75,000 - $100,000" },
  { value: "OneHundredKAndUp", label: "$100,000+" },
];

const SQUARE_FOOTAGE_RANGES = [
  { value: "UnderTwelveHundred", label: "Under 1,200 sq ft" },
  { value: "TwelveHundredToTwoThousand", label: "1,200 - 2,000 sq ft" },
  { value: "TwoThousandToThreeThousand", label: "2,000 - 3,000 sq ft" },
  { value: "OverThreeThousand", label: "Over 3,000 sq ft" },
];

const PROJECT_DURATIONS = [
  { value: "OneMonth", label: "1 Month" },
  { value: "TwoMonths", label: "2 Months" },
  { value: "ThreeMonths", label: "3 Months" },
  { value: "FourToSixMonths", label: "4-6 Months" },
  { value: "SeventToTwelveMonths", label: "7-12 Months" },
  { value: "MoreThanAYear", label: "Over 1 Year" },
];

const SPECIAL_REQUIREMENTS_OPTIONS = [
  { value: "AccessibilityForDisabilities", label: "Accessibility Features" },
  { value: "PetFriendlyDesign", label: "Pet-Friendly Design" },
  { value: "EnvironmentallySustainableMaterials", label: "Eco-Friendly/Sustainable" },
  { value: "SmartHomeIntegration", label: "Smart Home Integration" },
  { value: "UniqueLightingEffects", label: "Unique Lighting Effects" },
  { value: "SpecificAcousticTreatments", label: "Soundproofing" },
  { value: "CustomStorageSolutions", label: "Custom Storage Solutions" },
  { value: "ArtIntegration", label: "Art Integration" },
  { value: "HistoricalPreservationConsideration", label: "Historical Preservation" },
  { value: "FengShui", label: "Feng Shui" },
  { value: "OutdoorLivingConnections", label: "Outdoor Living Space" },
  { value: "HomeGym", label: "Home Gym" },
  { value: "WineCellar", label: "Wine Cellar" },
  { value: "SpecializedVentilationSystems", label: "Specialized Ventilation" },
  { value: "Other", label: "Other" },
];

export const BeforeAfterBasicInfoForm: React.FC<BeforeAfterBasicInfoFormProps> = ({
  formData,
  onFormDataChange,
}) => {
  const selectedSpecialRequirements = useMemo(
    () =>
      SPECIAL_REQUIREMENTS_OPTIONS.filter((option) =>
        formData.specialRequirements.includes(option.value)
      ),
    [formData.specialRequirements]
  );

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Stack spacing={3}>
          <TextField
            label="Project Name"
            required
            value={formData.projectName}
            onChange={(e) => onFormDataChange("projectName", e.target.value)}
            fullWidth
            variant="outlined"
          />

          <TextField
            label="Subtitle"
            required
            value={formData.subtitle}
            onChange={(e) => onFormDataChange("subtitle", e.target.value)}
            fullWidth
            variant="outlined"
            helperText="This appears under the project title on the frontend"
          />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="design-style-label">Design Style</InputLabel>
              <Select
                labelId="design-style-label"
                id="design-style"
                name="design-style"
                value={formData.style || ""}
                onChange={(e) => onFormDataChange("style", e.target.value || null)}
                label="Design Style"
              >
                {PROJECT_STYLES.map((style) => (
                  <MenuItem key={style.value} value={style.value}>
                    {style.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="project-category-label">Project Type</InputLabel>
              <Select
                labelId="project-category-label"
                id="project-category"
                name="project-category"
                value={formData.projectCategory || ""}
                onChange={(e) => onFormDataChange("projectCategory", e.target.value || null)}
                label="Project Type"
              >
                {PROJECT_CATEGORIES.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="budget-range-label">Budget Range</InputLabel>
              <Select
                labelId="budget-range-label"
                id="budget-range"
                name="budget-range"
                value={formData.budgetRange || ""}
                onChange={(e) => onFormDataChange("budgetRange", e.target.value || null)}
                label="Budget Range"
              >
                {BUDGET_RANGES.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="square-footage-label">Sq. Footage</InputLabel>
              <Select
                labelId="square-footage-label"
                id="square-footage"
                name="square-footage"
                value={formData.squareFootage || ""}
                onChange={(e) => onFormDataChange("squareFootage", e.target.value || null)}
                label="Sq. Footage"
              >
                {SQUARE_FOOTAGE_RANGES.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="project-duration-label">Project Duration</InputLabel>
              <Select
                labelId="project-duration-label"
                id="project-duration"
                name="project-duration"
                value={formData.projectDuration || ""}
                onChange={(e) => onFormDataChange("projectDuration", e.target.value || null)}
                label="Project Duration"
              >
                {PROJECT_DURATIONS.map((duration) => (
                  <MenuItem key={duration.value} value={duration.value}>
                    {duration.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              Special Requirements
            </Typography>
            <Autocomplete
              multiple
              options={SPECIAL_REQUIREMENTS_OPTIONS}
              getOptionLabel={(option) => option.label}
              value={selectedSpecialRequirements}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, newValue) => {
                onFormDataChange(
                  "specialRequirements",
                  newValue ? newValue.map((item) => item.value) : []
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.label}
                    {...getTagProps({ index })}
                    key={option.value}
                    sx={{ "& .MuiChip-label": { color: "#000 !important" } }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="special-requirements"
                  name="special-requirements"
                  placeholder="Select special requirements"
                />
              )}
            />
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default BeforeAfterBasicInfoForm;
